import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_TYPE_OPTIONS } from "@constants/policy-type-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PolicyTypes } from "@enums/policy-types";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn";
import { SelectOption } from "@idw-types/common";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import {
    getNewPoliciesFromPolicyType,
    getNewPolicyConfigFromPolicyType,
    getPolicyOptions,
    getPolicyTypeOption
} from "@utils/policies/policy-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { PolicyDetailsContainer } from "./policy-details-container/policy-details-container";
import styles from "./verification-service-details-policies-tab.less";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUIContent, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type VerificationServiceDetailsPoliciesTabContainerProps = {
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    validationErrors: PolicyDetailErrors;
    policyType: PolicyTypes;
    initialPolicyType: PolicyTypes;
    axnRpServices: Array<AxnRpService>;
    axnuiContent: AXNUIContent;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAcquiredAttributes: Array<AxnAcquiredAttribute>;
    axnCompanies: Array<AxnCompany>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    rpServiceID: string;
    workflowID: string;
    headerButton?: React.ReactNode;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const VerificationServiceDetailsPoliciesTabContainer = (props: VerificationServiceDetailsPoliciesTabContainerProps) => {
    const {
        isDisabled,
        isUsingThemedUI,
        validationErrors,
        policyType,
        initialPolicyType,
        axnRpServices,
        selectedLanguage,
        axnuiContent,
        multiLanguageAXNUIContent,
        availableLanguages,
        isMultiLanguageEnabled,
        axnAttributeTypes,
        axnAcquiredAttributes,
        axnCompanies,
        axnAttributeTypeFields,
        rpServiceConfigurationState,
        simpleRPServiceWorkflows,
        axnAvailableAssertionStatuses,
        rpServiceID,
        workflowID,
        headerButton,
        setRpServiceConfigurationState
    } = props;

    const handlePolicyOptionChange = (newValue: SelectOption) => {
        if (newValue.value !== selectedPolicyTypeOption?.value) {
            const newPolicies = getNewPoliciesFromPolicyType(
                rpServiceConfigurationState,
                newValue.value as PolicyTypes,
                rpServiceConfigurationState.policies
            );
            const newPolicyConfig = getNewPolicyConfigFromPolicyType(
                rpServiceConfigurationState,
                newValue.value as PolicyTypes,
                rpServiceConfigurationState.policyConfig
            );

            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                policies: newPolicies,
                policyConfig: newPolicyConfig,
                usePolicyManager: newValue.value === PolicyTypes.NONE ? "false" : "true"
            });
        }
    };


    const initialPolicyTypeOption = useMemo(() => {
        return getPolicyTypeOption(initialPolicyType);
    }, [initialPolicyType]);

    const selectedPolicyTypeOption = useMemo(() => {
        return getPolicyTypeOption(policyType);
    }, [POLICY_TYPE_OPTIONS, policyType]);

    const showPolicyDetails = useMemo(() => {
        if (selectedPolicyTypeOption) {
            return [
                PolicyTypes.ALWAYS_OBLIGATE,
                PolicyTypes.DECISION_ENGINE,
                PolicyTypes.TRUST_SCORE
            ].includes(selectedPolicyTypeOption.value as PolicyTypes);
        }

        return false;
    }, [selectedPolicyTypeOption?.value]);

    const policyOptions = useMemo(() => {
        return getPolicyOptions(selectedPolicyTypeOption, initialPolicyTypeOption);
    }, [selectedPolicyTypeOption, policyType]);

    return (
        <div className={styles.verificationServiceDetailsPoliciesTab}>
            <div>
                <ContentPageHeader
                    title={resources.pages.verificationServiceDetails.tabs.policies.sections.policies.title}
                    headerButton={headerButton}
                />
                <ContentPageBody isMaxWidth={true}>
                    <SelectFormGroup
                        controlId={"policies-tab-policy-select-input"}
                        automationId={"policies-tab-policy-select-input"}
                        label={resources.pages.verificationServiceDetails.tabs.policies.sections.policies.inputs.selectPolicyOption.label}
                        ariaLabel={resources.pages.verificationServiceDetails.tabs.policies.sections.policies.inputs.selectPolicyOption.label}
                        disabled={isDisabled}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={false}
                        layout={LayoutTypes.ROW}
                        labelWidth={LabelWidthTypes.MEDIUM}
                        onChange={handlePolicyOptionChange}
                        options={policyOptions}
                        selectedOption={selectedPolicyTypeOption}
                    />
                </ContentPageBody>
                {showPolicyDetails && (
                    <PolicyDetailsContainer
                        showTitle={true}
                        isDisabled={isDisabled}
                        isUsingThemedUI={isUsingThemedUI}
                        validationErrors={validationErrors}
                        axnCompanies={axnCompanies}
                        axnRpServices={axnRpServices}
                        selectedLanguage={selectedLanguage}
                        axnuiContent={axnuiContent}
                        multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                        availableLanguages={availableLanguages}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        axnAttributeTypes={axnAttributeTypes}
                        axnAcquiredAttributes={axnAcquiredAttributes}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        policyType={selectedPolicyTypeOption.value as PolicyTypes}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                        axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                        rpServiceID={rpServiceID}
                        workflowID={workflowID}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}

                    />
                )}
            </div>
        </div>
    );
};